import { tripNow } from '../clock.ts';

// Wall times in IANA zones (NFR-13): bookings store "YYYY-MM-DDTHH:MM" plus a zone, and durations
// come from the two instants, as ReservationTime does on the apps.

function offsetMs(at: number, tz: string): number {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(new Date(at))
      .map((p) => [p.type, p.value]),
  );
  const n = (k: string) => Number(parts[k] ?? 0);
  const wall = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'), n('second'));
  return wall - Math.floor(at / 1000) * 1000;
}

/** The instant a wall time in `tz` names (the earlier one across a DST overlap). */
export function instant(local: string | null, tz: string | null): number | null {
  const m = local ? /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(local) : null;
  if (!m) return null;
  const wall = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] ?? 0), Number(m[5] ?? 0));
  if (!tz) return wall;
  try {
    let at = wall - offsetMs(wall, tz);
    at = wall - offsetMs(at, tz);
    return at;
  } catch {
    return wall;
  }
}

const ABBREVIATIONS: Record<string, [string, string]> = {
  'Asia/Tokyo': ['JST', 'JST'],
  'Asia/Manila': ['PHT', 'PHT'],
  'Asia/Seoul': ['KST', 'KST'],
  'Asia/Taipei': ['CST', 'CST'],
  'Asia/Shanghai': ['CST', 'CST'],
  'Asia/Hong_Kong': ['HKT', 'HKT'],
  'Asia/Singapore': ['SGT', 'SGT'],
  'Asia/Bangkok': ['ICT', 'ICT'],
  'Asia/Ho_Chi_Minh': ['ICT', 'ICT'],
  'Europe/London': ['GMT', 'BST'],
  'Europe/Lisbon': ['WET', 'WEST'],
  'Europe/Dublin': ['GMT', 'IST'],
  'Europe/Bucharest': ['EET', 'EEST'],
  'Europe/Athens': ['EET', 'EEST'],
  'Europe/Sofia': ['EET', 'EEST'],
  'Europe/Helsinki': ['EET', 'EEST'],
  'Europe/Istanbul': ['TRT', 'TRT'],
  'Asia/Dubai': ['GST', 'GST'],
  'America/New_York': ['EST', 'EDT'],
  'America/Los_Angeles': ['PST', 'PDT'],
};

/** "JST", "WEST"; else the browser's short name ("GMT+9"). */
export function zoneLabel(tz: string | null, at: number | null): string {
  if (!tz) return '';
  const pair = ABBREVIATIONS[tz];
  const when = at ?? tripNow();
  if (pair) {
    const jan = offsetMs(Date.UTC(new Date(when).getUTCFullYear(), 0, 1), tz);
    return offsetMs(when, tz) !== jan ? pair[1] : pair[0];
  }
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
        .formatToParts(new Date(when))
        .find((p) => p.type === 'timeZoneName')?.value ?? tz
    );
  } catch {
    return tz;
  }
}

export const duration = (minutes: number) =>
  `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
